import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import {
  IconButton,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import SouthIcon from '@mui/icons-material/South';
import CategoryPlusIcon from '@mui/icons-material/Category';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faIndustry, faTruckRampBox } from '@fortawesome/free-solid-svg-icons';
import PurchaseOrderPlusIcon from '../Icons/PurchaseOrderPlusIcon';
import VendorSlipIcon from '../Icons/VendorSlipIcon';

type Props = {
  selectedIds: GridRowSelectionModel;
  clearSelection: () => void;

  items: any[];
  setItems: React.Dispatch<React.SetStateAction<any[]>>;

  // Formik instance that owns items[]
  formik: any;

  viewMode: boolean;
  onAddRow: () => void;
  importItemClick: () => void;
  createItemClick: () => void;
  handleBulkBrowsePurchaseOrder?: () => void;
  handleBulkCreatePurchaseOrder?: () => void;
  handleBulkBrowseVendor?: () => void;
  handleBulkCreateVendor?: () => void;
};

const BulkActionsMenu: React.FC<Props> = ({
  selectedIds,
  clearSelection,
  items,
  setItems,
  formik,
  viewMode,
  onAddRow,
  importItemClick,
  createItemClick,
  handleBulkBrowsePurchaseOrder,
  handleBulkCreatePurchaseOrder,
  handleBulkBrowseVendor,
  handleBulkCreateVendor,
}) => {
  const theme = useTheme();
  const { t: tGB } = useTranslation([], { keyPrefix: 'team.Global.Buttons' });
  const { t: tMOCA } = useTranslation([], {
    keyPrefix: 'team.Modals.Sales Orders.createAndEdit.Add Sales Order',
  });

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const hasSelection = selectedIds.length > 0;
  const selectedSet = React.useMemo(
    () => new Set(selectedIds as (string | number)[]),
    [selectedIds],
  );

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const pushItems = (next: any[]) => {
    setItems(next);
    formik?.setFieldValue?.('items', next);
  };

  const handleDeleteSelected = () => {
    if (!hasSelection) return;
    const next = items.filter((row) => !selectedSet.has(row.itemId));
    pushItems(next);
    clearSelection();
    handleClose();
  };

  const disabled = viewMode;

  const hasBulkActions =
    !!handleBulkBrowsePurchaseOrder ||
    !!handleBulkCreatePurchaseOrder ||
    !!handleBulkBrowseVendor ||
    !!handleBulkCreateVendor;

  return (
    <>
      <IconButton
        size="small"
        onClick={handleOpen}
        disabled={disabled}
        color="primary"
        sx={{ ml: 1 }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem disabled={disabled} onClick={onAddRow}>
          <ListItemIcon>
            <AddIcon color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={tGB('Add Row')} />
        </MenuItem>
        <Divider />
        <MenuItem disabled={disabled} onClick={importItemClick}>
          <ListItemIcon>
            <SouthIcon color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={tMOCA('Import Items')} />
        </MenuItem>

        <MenuItem disabled={disabled} onClick={createItemClick}>
          <ListItemIcon>
            <CategoryPlusIcon color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={tMOCA('New Item')} />
        </MenuItem>
        {hasBulkActions && [
          <Divider key="bulk-divider">
            <Typography variant="caption">
              {tMOCA('Bulk actions on selected')}
            </Typography>
          </Divider>,

          handleBulkBrowsePurchaseOrder && (
            <MenuItem
              key="bulk-browse-po"
              disabled={!hasSelection || disabled}
              onClick={handleBulkBrowsePurchaseOrder}
            >
              <ListItemIcon>
                <FontAwesomeIcon
                  color={theme.palette.primary.main}
                  icon={faTruckRampBox}
                />
              </ListItemIcon>
              <ListItemText primary={tMOCA('Change Purchase Order')} />
            </MenuItem>
          ),

          handleBulkCreatePurchaseOrder && (
            <MenuItem
              key="bulk-create-po"
              disabled={!hasSelection || disabled}
              onClick={handleBulkCreatePurchaseOrder}
            >
              <ListItemIcon>
                <PurchaseOrderPlusIcon
                  style={{ color: theme.palette.primary.main }}
                />
              </ListItemIcon>
              <ListItemText primary={tMOCA('Change with new Purchase Order')} />
            </MenuItem>
          ),

          handleBulkBrowseVendor && (
            <MenuItem
              key="bulk-browse-vendor"
              disabled={!hasSelection || disabled}
              onClick={handleBulkBrowseVendor}
            >
              <ListItemIcon>
                <FontAwesomeIcon
                  color={theme.palette.primary.main}
                  icon={faIndustry}
                />
              </ListItemIcon>
              <ListItemText primary={tMOCA('Change Vendor')} />
            </MenuItem>
          ),

          handleBulkCreateVendor && (
            <MenuItem
              key="bulk-create-vendor"
              disabled={!hasSelection || disabled}
              onClick={handleBulkCreateVendor}
            >
              <ListItemIcon>
                <VendorSlipIcon style={{ color: theme.palette.primary.main }} />
              </ListItemIcon>
              <ListItemText primary={tMOCA('Change with new Vendor')} />
            </MenuItem>
          ),
        ]}

        <Divider />
        <MenuItem
          disabled={!hasSelection || disabled}
          onClick={handleDeleteSelected}
        >
          <ListItemIcon>
            <DeleteOutlineIcon color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Delete selected rows" />
        </MenuItem>
      </Menu>
    </>
  );
};

export default BulkActionsMenu;
